import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  InputAdornment,
} from '@mui/material';
import {
  AccountBalance as BankIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { financeService } from '../services/financeService';
import { useSubmitLock } from '../hooks/useSubmitLock';

function BanksTab() {
  const [submitting, runSubmit] = useSubmitLock();
  const [banks, setBanks] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loading, setLoading] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState(undefined);

  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState('create');
  const [currentBank, setCurrentBank] = useState(null);

  const [formData, setFormData] = useState({
    code: '',
    name: '',
    is_active: true,
    notes: ''
  });

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const loadBanks = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await financeService.getBanksPage({
        q: searchQuery,
        is_active: activeFilter,
        offset: page * rowsPerPage,
        limit: rowsPerPage,
      });
      setBanks(response.data || []);
      setTotal(response.total || 0);
    } catch (err) {
      console.error('Erro ao carregar bancos:', err);
      setError('Erro ao carregar bancos. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchQuery, activeFilter]);

  useEffect(() => {
    loadBanks();
  }, [loadBanks]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  const handleActiveFilter = (event) => {
    const value = event.target.value;
    setActiveFilter(value === '' ? undefined : value === 'true');
    setPage(0);
  };

  const handleOpenDialog = (mode, bank = null) => {
    setDialogMode(mode);
    setCurrentBank(bank);

    if (mode === 'edit' && bank) {
      setFormData({
        code: bank.code || '',
        name: bank.name || '',
        is_active: bank.is_active !== undefined ? bank.is_active : true,
        notes: bank.notes || ''
      });
    } else {
      setFormData({
        code: '',
        name: '',
        is_active: true,
        notes: ''
      });
    }

    setOpenDialog(true);
    setError('');
    setSuccess('');
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentBank(null);
    setError('');
    setSuccess('');
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const cleanFormData = (data) => {
    const cleaned = {};
    Object.keys(data).forEach(key => {
      const value = data[key];
      if (typeof value === 'boolean') {
        cleaned[key] = value;
      } else if (value !== '' && value !== null && value !== undefined) {
        cleaned[key] = value;
      }
    });
    return cleaned;
  };

  const handleSubmit = async () => {
    setError('');
    setSuccess('');

    if (!formData.name.trim()) {
      setError('Nome é obrigatório');
      return;
    }

    try {
      const payload = cleanFormData({
        ...formData,
        code: formData.code ? String(formData.code).trim() : ''
      });

      if (dialogMode === 'create') {
        await financeService.createBank(payload);
        setSuccess('Banco criado com sucesso!');
      } else {
        await financeService.updateBank(currentBank.id, payload);
        setSuccess('Banco atualizado com sucesso!');
      }

      handleCloseDialog();
      loadBanks();
    } catch (err) {
      console.error('Erro ao salvar banco:', err);
      const errorMessage = err.response?.data?.detail
        || (Array.isArray(err.response?.data) ? err.response.data.map(e => e.msg).join(', ') : null)
        || 'Erro ao salvar banco. Tente novamente.';
      setError(errorMessage);
    }
  };

  const handleDelete = async (bank) => {
    if (!window.confirm(`Tem certeza que deseja excluir o banco "${bank.name}"?`)) {
      return;
    }

    try {
      await financeService.deleteBank(bank.id);
      setSuccess('Banco excluído com sucesso!');
      loadBanks();
    } catch (err) {
      console.error('Erro ao excluir banco:', err);
      setError(err.response?.data?.detail || 'Erro ao excluir banco. Tente novamente.');
    }
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Card
        elevation={0}
        sx={{
          mb: 3,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          borderRadius: 3
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <BankIcon sx={{ fontSize: 32 }} />
                <Typography variant="h5" fontWeight="bold">
                  Bancos
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Cadastro de bancos para uso no financeiro
              </Typography>
            </Box>

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog('create')}
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.3)' },
              }}
            >
              Novo Banco
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <TextField
          placeholder="Buscar por nome ou código..."
          value={searchQuery}
          onChange={handleSearch}
          size="small"
          sx={{ minWidth: 280 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            )
          }}
        />

        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Ativo</InputLabel>
          <Select value={activeFilter === undefined ? '' : String(activeFilter)} label="Ativo" onChange={handleActiveFilter}>
            <MenuItem value="">Todos</MenuItem>
            <MenuItem value="true">Ativos</MenuItem>
            <MenuItem value="false">Inativos</MenuItem>
          </Select>
        </FormControl>

        {(searchQuery || activeFilter !== undefined) && (
          <Chip
            label="Limpar"
            onClick={() => {
              setSearchQuery('');
              setActiveFilter(undefined);
              setPage(0);
            }}
            variant="outlined"
          />
        )}
      </Box>

      <TableContainer component={Box} sx={{ border: '1px solid rgba(0,0,0,0.12)', borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell><strong>Código</strong></TableCell>
              <TableCell><strong>Nome</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell><strong>Observações</strong></TableCell>
              <TableCell align="right"><strong>Ações</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : banks.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">Nenhum banco encontrado</Typography>
                </TableCell>
              </TableRow>
            ) : (
              banks.map((bank) => (
                <TableRow key={bank.id} hover>
                  <TableCell>{bank.code || '-'}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <BankIcon fontSize="small" color="action" />
                      <Typography variant="body2" fontWeight={600}>{bank.name}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={bank.is_active ? 'Ativo' : 'Inativo'}
                      color={bank.is_active ? 'success' : 'default'}
                      variant={bank.is_active ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 380 }} noWrap>
                      {bank.notes || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Editar">
                      <Button
                        size="small"
                        startIcon={<EditIcon fontSize="small" />}
                        onClick={() => handleOpenDialog('edit', bank)}
                      >
                        Editar
                      </Button>
                    </Tooltip>
                    <Tooltip title="Excluir">
                      <Button
                        size="small"
                        color="error"
                        startIcon={<DeleteIcon fontSize="small" />}
                        onClick={() => handleDelete(bank)}
                      >
                        Excluir
                      </Button>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[10, 25, 50, 100]}
        labelRowsPerPage="Linhas"
      />

      <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="sm">
        <DialogTitle>
          {dialogMode === 'create' ? 'Novo Banco' : 'Editar Banco'}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'grid', gap: 2 }}>
            <TextField
              label="Código"
              value={formData.code}
              onChange={(e) => handleFormChange('code', e.target.value)}
              placeholder="Ex.: 001"
              helperText="Sem código, não é possível cadastrar dois bancos com o mesmo nome."
              inputProps={{ maxLength: 10 }}
            />
            <TextField
              label="Nome"
              value={formData.name}
              onChange={(e) => handleFormChange('name', e.target.value)}
              required
            />
            <TextField
              label="Observações"
              value={formData.notes}
              onChange={(e) => handleFormChange('notes', e.target.value)}
              multiline
              minRows={3}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Switch
                checked={!!formData.is_active}
                onChange={(e) => handleFormChange('is_active', e.target.checked)}
              />
              <Typography variant="body2">Ativo</Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button variant="contained" onClick={() => runSubmit(handleSubmit)} disabled={submitting}>
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default BanksTab;
